import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  Plus,
  X,
  Sparkles,
  FileText,
  Calculator,
  Languages,
  PenTool,
  BarChart3,
  MessageSquare,
  Code,
  Lightbulb,
  GripVertical,
} from 'lucide-react'
import { type AgentBuilderState } from '../AgentBuilder'
import { type SkillLibraryItem, type SkillCategory } from '@/lib/api/services/aiStudio'

interface SkillsStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
}

// Extended skill type with display properties
interface DisplaySkill extends SkillLibraryItem {
  displayIcon?: string
}

// Map categories to icons
const CATEGORY_ICONS: Record<SkillCategory, React.ElementType> = {
  ContentGeneration: PenTool,
  DataAnalysis: BarChart3,
  Communication: MessageSquare,
  Research: FileText,
  Automation: Code,
  Custom: Sparkles,
}

// Mock skill library data
const SKILL_LIBRARY: DisplaySkill[] = [
  {
    id: '1',
    slug: 'content-generation',
    name: 'Content Generation',
    description: 'Generate high-quality written content including articles, blog posts, and marketing copy',
    category: 'ContentGeneration',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'PenTool',
  },
  {
    id: '2',
    slug: 'text-summarization',
    name: 'Text Summarization',
    description: 'Summarize long documents into concise, key-point summaries',
    category: 'DataAnalysis',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'FileText',
  },
  {
    id: '3',
    slug: 'data-analysis',
    name: 'Data Analysis',
    description: 'Analyze datasets and extract meaningful insights and patterns',
    category: 'DataAnalysis',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'BarChart3',
  },
  {
    id: '4',
    slug: 'translation',
    name: 'Translation',
    description: 'Translate text between multiple languages with high accuracy',
    category: 'Communication',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'Languages',
  },
  {
    id: '5',
    slug: 'code-generation',
    name: 'Code Generation',
    description: 'Generate code snippets and full functions in various programming languages',
    category: 'Automation',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'Code',
  },
  {
    id: '6',
    slug: 'calculations',
    name: 'Calculations',
    description: 'Perform mathematical calculations and formula-based computations',
    category: 'DataAnalysis',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'Calculator',
  },
  {
    id: '7',
    slug: 'conversation',
    name: 'Conversation',
    description: 'Engage in natural, context-aware conversations with users',
    category: 'Communication',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'MessageSquare',
  },
  {
    id: '8',
    slug: 'creative-ideation',
    name: 'Creative Ideation',
    description: 'Generate creative ideas and brainstorm solutions to problems',
    category: 'Custom',
    systemPromptFragment: '',
    requiresApiKey: false,
    isBuiltIn: true,
    usageCount: 0,
    displayIcon: 'Lightbulb',
  },
]

const ICON_MAP: Record<string, React.ElementType> = {
  PenTool,
  FileText,
  BarChart3,
  Languages,
  Code,
  Calculator,
  MessageSquare,
  Lightbulb,
  Sparkles,
}

const CATEGORIES: Array<'All' | SkillCategory> = ['All', 'ContentGeneration', 'DataAnalysis', 'Communication', 'Research', 'Automation', 'Custom']

const CATEGORY_LABELS: Record<SkillCategory | 'All', string> = {
  All: 'All',
  ContentGeneration: 'Content',
  DataAnalysis: 'Analysis',
  Communication: 'Communication',
  Research: 'Research',
  Automation: 'Automation',
  Custom: 'Custom',
}

export function SkillsStep({ state, updateState }: SkillsStepProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<'All' | SkillCategory>('All')

  const filteredSkills = SKILL_LIBRARY.filter((skill) => {
    const matchesSearch =
      skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      skill.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || skill.category === selectedCategory
    const notSelected = !state.selectedSkills.some((s) => s.id === skill.id)
    return matchesSearch && matchesCategory && notSelected
  })

  const handleAddSkill = (skill: DisplaySkill) => {
    // Convert to SkillLibraryItem by removing displayIcon
    const { displayIcon, ...skillItem } = skill
    updateState({ selectedSkills: [...state.selectedSkills, skillItem] })
  }

  const handleRemoveSkill = (skillId: string) => {
    updateState({ selectedSkills: state.selectedSkills.filter((s) => s.id !== skillId) })
  }

  const getIconForSkill = (skill: SkillLibraryItem | DisplaySkill): React.ElementType => {
    // Check if it's a DisplaySkill with displayIcon
    if ('displayIcon' in skill && skill.displayIcon) {
      return ICON_MAP[skill.displayIcon] || CATEGORY_ICONS[skill.category] || Sparkles
    }
    // Fall back to category icon
    return CATEGORY_ICONS[skill.category] || Sparkles
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Skills & Capabilities</h2>
        <p className="text-gray-500 dark:text-gray-400">
          Select the skills and capabilities your agent should have.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Skill Library */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">Skill Library</h3>
            <Button variant="ghost" size="sm" className="text-purple-600">
              <Plus className="mr-1 h-4 w-4" />
              Create Custom
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search skills..."
              className="pl-10 rounded-xl"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {CATEGORY_LABELS[category]}
              </button>
            ))}
          </div>

          {/* Available Skills */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {filteredSkills.map((skill) => {
              const Icon = getIconForSkill(skill)

              return (
                <div
                  key={skill.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all cursor-pointer"
                  onClick={() => handleAddSkill(skill)}
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/30 dark:to-violet-900/30">
                    <Icon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{skill.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {skill.description}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {CATEGORY_LABELS[skill.category]}
                  </Badge>
                  <Plus className="h-4 w-4 text-gray-400" />
                </div>
              )
            })}
            {filteredSkills.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No skills found</p>
              </div>
            )}
          </div>
        </div>

        {/* Selected Skills */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Selected Skills ({state.selectedSkills.length})
            </h3>
            {state.selectedSkills.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600"
                onClick={() => updateState({ selectedSkills: [] })}
              >
                Clear All
              </Button>
            )}
          </div>

          <div className="min-h-[400px] rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-4">
            {state.selectedSkills.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                  <Sparkles className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-1">No skills selected</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Click on skills from the library to add them
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {state.selectedSkills.map((skill, idx) => {
                  const Icon = getIconForSkill(skill)

                  return (
                    <div
                      key={skill.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
                    >
                      <button
                        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <div className="p-2 rounded-lg bg-purple-500">
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{skill.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {skill.description}
                        </p>
                      </div>
                      <Badge className="bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 text-xs">
                        #{idx + 1}
                      </Badge>
                      <button
                        onClick={() => handleRemoveSkill(skill.id)}
                        className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Skills Summary */}
          {state.selectedSkills.length > 0 && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <strong>{state.selectedSkills.length}</strong> skill
                {state.selectedSkills.length !== 1 && 's'} selected across{' '}
                <strong>
                  {new Set(state.selectedSkills.map((s) => s.category)).size}
                </strong>{' '}
                categor{new Set(state.selectedSkills.map((s) => s.category)).size !== 1 ? 'ies' : 'y'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
